// React
import * as React from "react";

// Date Packages
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

// Components
import { Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { StyledErrorMessage } from "src/components/application/styled-components/DropZone";

// Store
import { useAppDispatch } from "src/store/store";

// Slices
import { setEndDate, setStartDate, useDcaSlice } from "src/store/features/dca.slice";
import { trackEvent } from "src/Amplitude";

dayjs.extend(utc);
dayjs.extend(timezone);

type ScheduleDatePickerProps = {
  source: "start date" | "end date";
};

export function ScheduleDatePicker({ source }: ScheduleDatePickerProps) {
  const { startDate } = useDcaSlice();
  const dispatch = useAppDispatch();
  const [error, setError] = React.useState("");

  const today = new Date();
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = dayjs(today).tz(userTimeZone);
  const defaultSelection = source === "start date" ? formattedDate : formattedDate.add(1, "day");

  const [value, setValue] = React.useState<Dayjs>(defaultSelection);

  React.useEffect(() => {
    const selectedDate = value.endOf("day").valueOf();
    if (source === "start date" && value) {
      dispatch(setStartDate(selectedDate));
      trackEvent("Changed start date", { selectedDate }, "DCA");
    }
    if (source === "end date" && value) {
      dispatch(setEndDate(selectedDate));
      trackEvent("Changed end date", { selectedDate }, "DCA");
    }
  }, [value]);

  // if start date picker is selected, set the min date to today
  // if end date picker is selected, set the min date to the day after the start date
  const minDate =
    source === "start date" ? formattedDate : dayjs(startDate).tz(userTimeZone).add(1, "day");

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" flexDirection="column" alignItems="center">
        <DatePicker
          value={value}
          showDaysOutsideCurrentMonth
          format="DD MMM YYYY"
          onError={(reason) => {
            if (reason) setError(reason);
          }}
          onChange={(newValue) => {
            if (newValue) setValue(newValue as Dayjs);
            setError("");
          }}
          minDate={minDate}
          sx={{
            ".MuiOutlinedInput-input": {
              padding: "0",
              fontSize: "16px",
              fontFamily: "Roobert",
              lineHeight: "20px",
              width: "auto",
            },
            ".MuiIconButton-root": {
              padding: "0",
              margin: "0",
            },
            ".MuiOutlinedInput-root": {
              padding: "8px 12px",
              borderRadius: "16px",
              backgroundColor: "#F8F9F9",
              width: "fit-content",
              maxWidth: "145px",
              display: "flex",
              alignItems: "center",
            },
            ".MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            ".MuiInputAdornment-root": {
              margin: "0",
            },
          }}
        />
        {error === "minDate" && (
          <StyledErrorMessage>{`End date needs to be after start date`} </StyledErrorMessage>
        )}
      </Box>
    </LocalizationProvider>
  );
}
